import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth, OFFICE_AUTO_TIMECARD_ROLES } from '@/context/AuthContext';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Play, Square, Plus, Trash2, User, MoreHorizontal, Filter, Zap } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function TimecardsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAutoRole = OFFICE_AUTO_TIMECARD_ROLES.includes(user?.role);
  const canManageTimecards = ['admin', 'superadmin', 'office'].includes(user?.role);
  const [timecards, setTimecards] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [clockInDialogOpen, setClockInDialogOpen] = useState(false);
  const [clockInForm, setClockInForm] = useState({ job_id: '', notes: '' });
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [manualForm, setManualForm] = useState({ user_id: '', job_id: '', clock_in: '', clock_out: '', notes: '' });
  const [reportFilters, setReportFilters] = useState({ user_id: 'all', date_from: '', date_to: '' });

  const fetchData = useCallback(async (filters = {}) => {
    try {
      const params = {};
      if (filters.user_id && filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [tcRes, activeRes, usersRes, jobsRes] = await Promise.all([
        api.get('/timecards', { params }),
        api.get('/timecards/active'),
        api.get('/timecards/users'),
        api.get('/jobs', { params: { page_size: 500 } })
      ]);
      setTimecards(tcRes.data);
      setActiveCard(activeRes.data);
      setUsers(usersRes.data);
      setJobs(jobsRes.data.items || jobsRes.data);
    } catch { toast.error(t('timecards.errorLoading')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApplyFilters = () => { setLoading(true); fetchData(reportFilters); };
  const handleClearFilters = () => {
    const cleared = { user_id: 'all', date_from: '', date_to: '' };
    setReportFilters(cleared);
    setLoading(true);
    fetchData(cleared);
  };

  const filteredTotalHours = timecards.reduce((sum, tc) => sum + (tc.total_hours || 0), 0);

  // Alian thinks in hours+minutes ("1h 21m"), not decimals ("1.35h") — single
  // source of truth is always filteredTotalHours (the exact rows on screen).
  const fmtHours = (h) => {
    const total = Math.max(0, Math.round((h || 0) * 60));
    return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, '0')}m`;
  };

  const handleClockIn = async (e) => {
    e.preventDefault();
    try {
      await api.post('/timecards/clock-in', clockInForm);
      toast.success(t('timecards.clockInRegistered'));
      setClockInDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('timecards.clockInError')); }
  };

  const handleClockOut = async () => {
    try {
      await api.post('/timecards/clock-out', { notes: clockOutNotes });
      toast.success(t('timecards.clockOutRegistered'));
      setClockOutNotes('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('timecards.clockOutError')); }
  };

  const handleManualCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/timecards/manual', manualForm);
      toast.success(t('timecards.manualCreated'));
      setManualDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('timecards.manualError')); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/timecards/${id}`); toast.success(t('timecards.recordDeleted')); fetchData(); }
    catch { toast.error(t('timecards.deleteError')); }
  };

  const getJobNumber = (id) => { const j = jobs.find(j => j.id === id); return j ? j.job_number : '-'; };

  const formatTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('es-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getElapsed = () => {
    if (!activeCard?.clock_in) return '0:00';
    const start = new Date(activeCard.clock_in);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60);
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return `${h}:${String(m).padStart(2, '0')}`;
  };

  return (
    <div data-testid="timecards-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-[#3399FF]" /> {t('timecards.title')}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('timecards.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {canManageTimecards && (
            <Button data-testid="manual-entry-btn" variant="outline" size="sm" onClick={() => { setManualForm({ user_id: '', job_id: '', clock_in: '', clock_out: '', notes: '' }); setManualDialogOpen(true); }} className="border-[#003366]/20 text-[#003366]">
              <Plus className="w-4 h-4 mr-2" /> {t('timecards.manualEntry')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <Card className={`border-2 ${activeCard ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-100 bg-white'}`}>
          <CardContent className="p-4 sm:p-6">
            {activeCard ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="text-xs sm:text-sm font-semibold text-emerald-700 uppercase tracking-wider">{t('timecards.working')}</span>
                    {isAutoRole && (
                      <span data-testid="auto-session-tag" className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#3399FF] bg-[#3399FF]/10 px-1.5 py-0.5 rounded font-semibold">
                        <Zap className="w-3 h-3" /> {t('timecards.sourceAuto')}
                      </span>
                    )}
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900 font-mono">{getElapsed()}</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    {t('timecards.startTime')}: {formatTime(activeCard.clock_in)}
                    {activeCard.job_id && <span className="ml-2">| {getJobNumber(activeCard.job_id)}</span>}
                  </p>
                </div>
                {isAutoRole ? (
                  <p data-testid="auto-session-active-msg" className="text-xs sm:text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 max-w-[220px]">
                    {t('timecards.autoSessionDesc')}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2 w-full sm:w-auto">
                    <Input
                      data-testid="clock-out-notes"
                      value={clockOutNotes}
                      onChange={e => setClockOutNotes(e.target.value)}
                      placeholder={t('timecards.exitNotes')}
                      className="sm:w-48 h-9 bg-white"
                    />
                    <Button data-testid="clock-out-btn" onClick={handleClockOut} className="bg-red-600 hover:bg-red-700 text-white">
                      <Square className="w-4 h-4 mr-2" /> {t('timecards.clockOut')}
                    </Button>
                  </div>
                )}
              </div>
            ) : isAutoRole ? (
              <div className="flex items-center gap-3" data-testid="auto-session-inactive-msg">
                <span className="w-3 h-3 bg-gray-300 rounded-full" />
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-700">{t('timecards.autoSessionActive')}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('timecards.autoSessionDesc')}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-700">{t('timecards.notClockedIn')}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t('timecards.startClockIn')}</p>
                </div>
                <Button data-testid="clock-in-btn" size="sm" onClick={() => { setClockInForm({ job_id: '', notes: '' }); setClockInDialogOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Play className="w-4 h-4 mr-2" /> {t('timecards.clockIn')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reporte por empleado y periodo */}
      <Card className="bg-white border-gray-100">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-[#003366]" />
            <p className="text-sm font-semibold text-gray-700">{t('timecards.reportTitle')}</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-400">{t('timecards.filterByEmployee')}</Label>
              <Select value={reportFilters.user_id} onValueChange={v => setReportFilters({ ...reportFilters, user_id: v })}>
                <SelectTrigger data-testid="report-employee-filter" className="h-9 w-48 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('timecards.allEmployees')}</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-400">{t('timecards.dateFrom')}</Label>
              <Input data-testid="report-date-from" type="date" value={reportFilters.date_from} onChange={e => setReportFilters({ ...reportFilters, date_from: e.target.value })} className="h-9 w-40 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-gray-400">{t('timecards.dateTo')}</Label>
              <Input data-testid="report-date-to" type="date" value={reportFilters.date_to} onChange={e => setReportFilters({ ...reportFilters, date_to: e.target.value })} className="h-9 w-40 text-sm" />
            </div>
            <Button data-testid="apply-report-filter-btn" size="sm" onClick={handleApplyFilters} className="bg-[#003366] hover:bg-[#004080] text-white h-9">
              <Filter className="w-3.5 h-3.5 mr-1.5" /> {t('timecards.applyFilter')}
            </Button>
            <Button data-testid="clear-report-filter-btn" size="sm" variant="ghost" onClick={handleClearFilters} className="h-9 text-gray-500">
              {t('common.clear', { defaultValue: 'Limpiar' })}
            </Button>
            <div className="ml-auto text-right" data-testid="report-total-hours">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">{t('timecards.totalHoursFiltered')}</p>
              <p data-testid="filtered-total-hours" className="text-xl font-bold text-[#003366]">{fmtHours(filteredTotalHours)}</p>
            </div>
          </div>
        </CardContent>
      </Card>


      <Card className="bg-white border-gray-100 shadow-sm overflow-hidden hidden md:block">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-400">{t('common.loading')}</div>
          ) : timecards.length === 0 ? (
            <div className="py-16 text-center">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium">{t('timecards.noTimecards')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColEmployee')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColJob')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColIn')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColOut')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColHours')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColSource')}</th>
                    <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColStatus')}</th>
                    <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('timecards.hColActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {timecards.map((tc) => (
                    <tr key={tc.id} data-testid={`timecard-row-${tc.id}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-[#003366]/10 flex items-center justify-center">
                            <User className="w-3.5 h-3.5 text-[#003366]" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{tc.user_name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{tc.job_id ? getJobNumber(tc.job_id) : '-'}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{formatTime(tc.clock_in)}</td>
                      <td className="px-5 py-3.5 text-sm text-gray-600">{formatTime(tc.clock_out)}</td>
                      <td className="px-5 py-3.5 text-right text-sm font-semibold text-gray-700">{fmtHours(tc.total_hours)}</td>
                      <td className="px-5 py-3.5">
                        <span data-testid={`timecard-source-${tc.id}`} className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${tc.source === 'auto' ? 'bg-[#3399FF]/10 text-[#3399FF]' : 'bg-gray-100 text-gray-500'}`}>
                          {tc.source === 'auto' ? t('timecards.sourceAuto') : t('timecards.sourceManual')}
                        </span>
                      </td>
                      <td className="px-5 py-3.5"><StatusBadge status={tc.status} /></td>
                      <td className="px-5 py-3.5 text-right">
                        {canManageTimecards && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(tc.id)} className="h-8 w-8 text-red-400 hover:text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-white rounded-xl border border-gray-100 animate-pulse" />)}</div>
        ) : timecards.length === 0 ? (
          <Card className="bg-white border-gray-100"><CardContent className="py-12 text-center">
            <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm font-medium">{t('timecards.noTimecards')}</p>
          </CardContent></Card>
        ) : (
          timecards.map((tc, idx) => (
            <Card key={tc.id} data-testid={`timecard-mobile-${tc.id}`} className="bg-white border-gray-100 shadow-sm mobile-card-enter" style={{ animationDelay: `${idx * 50}ms` }}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#003366]/10 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-[#003366]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{tc.user_name}</p>
                      {tc.job_id && <p className="text-xs text-gray-400">{getJobNumber(tc.job_id)}</p>}
                    </div>
                  </div>
                  {canManageTimecards && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2 -mt-1"><MoreHorizontal className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDelete(tc.id)} className="text-red-600"><Trash2 className="w-3.5 h-3.5 mr-2" />{t('common.delete')}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3 text-gray-500">
                    <span>{formatTime(tc.clock_in)}</span>
                    <span>-</span>
                    <span>{tc.clock_out ? formatTime(tc.clock_out) : <span className="text-emerald-500 font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />{t('timecards.activeLabel')}</span>}</span>
                  </div>
                  <span className="text-sm font-bold text-[#003366]">{tc.total_hours != null ? fmtHours(tc.total_hours) : '-'}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={clockInDialogOpen} onOpenChange={setClockInDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-500" /> {t('timecards.clockInTitle')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClockIn} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('timecards.jobOptional')}</Label>
              <Select value={clockInForm.job_id} onValueChange={v => setClockInForm({ ...clockInForm, job_id: v })}>
                <SelectTrigger><SelectValue placeholder={t('timecards.noJobAssigned')} /></SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => ['scheduled', 'in_progress'].includes(j.status)).map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} - {j.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Input value={clockInForm.notes} onChange={e => setClockInForm({ ...clockInForm, notes: e.target.value })} placeholder={t('timecards.entryNotes')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setClockInDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="confirm-clock-in-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">{t('timecards.confirmStart')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Barlow'] text-xl">{t('timecards.manualTitle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleManualCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('timecards.employee')} *</Label>
              <Select value={manualForm.user_id} onValueChange={v => setManualForm({ ...manualForm, user_id: v })}>
                <SelectTrigger data-testid="manual-user-select"><SelectValue placeholder={t('timecards.selectEmployee')} /></SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('timecards.job')}</Label>
              <Select value={manualForm.job_id} onValueChange={v => setManualForm({ ...manualForm, job_id: v })}>
                <SelectTrigger><SelectValue placeholder={t('timecards.noJobAssigned')} /></SelectTrigger>
                <SelectContent>
                  {jobs.map(j => (
                    <SelectItem key={j.id} value={j.id}>{j.job_number} - {j.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('timecards.startLabel')} *</Label>
                <Input data-testid="manual-clock-in" type="datetime-local" value={manualForm.clock_in} onChange={e => setManualForm({ ...manualForm, clock_in: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('timecards.endLabel')} *</Label>
                <Input data-testid="manual-clock-out" type="datetime-local" value={manualForm.clock_out} onChange={e => setManualForm({ ...manualForm, clock_out: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('timecards.notes')}</Label>
              <Input value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setManualDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-manual-entry-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">{t('common.save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
